import {
  getHotelBreakdownItems,
  PaymentMethod,
} from "@apac/components/checkout";
import {
  ApacHotelsComponent,
  BasePriceBreakdown,
  type BasePriceBreakdownProps,
  TotalAmountLines,
  type TotalAmountLinesProps,
  useApacHotelsStyles,
} from "@apac/components/hotels";
import {
  RewardsAndCreditsBreakdownSection,
  type RewardsAndCreditsLineItem,
} from "@apac/hotels";
import { useModuleBEM } from "@b2bportal/core-themes";
import type { PlatformComponentProps } from "@b2bportal/core-types";
import { useCheckoutStateSelector } from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import clsx from "clsx";
import defaultStyles from "./styles.module.scss";

interface PriceBreakdownSectionProps
  extends PlatformComponentProps,
    BasePriceBreakdownProps,
    TotalAmountLinesProps {
  rewardsAndCreditsLineItems?: RewardsAndCreditsLineItem[];
  cardDisplayName?: string;
}

const COMPONENT_KEY = ApacHotelsComponent.PriceBreakdownReviewSection;

export const PriceBreakdownSection = ({
  className,
  children,
  rewardsAndCreditsLineItems = [],
  cardDisplayName,
  ...props
}: PriceBreakdownSectionProps) => {
  const styles = useApacHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t, formatFiatCurrency } = useI18nContext();
  const { payLaterBreakdownLocalCurrency } = useCheckoutStateSelector(
    getHotelBreakdownItems
  );

  return (
    <div className={clsx(block, className)}>
      <div className={cn("title")}>{t("priceBreakdown")}</div>

      <BasePriceBreakdown
        {...props}
        showPayLaterBreakdown
        className={cn("divider")}
      />

      <RewardsAndCreditsBreakdownSection
        lineItems={rewardsAndCreditsLineItems}
      />

      <TotalAmountLines {...props} className={cn("divider")} />

      <PaymentMethod displayName={cardDisplayName} />

      {/* Often used for additional content such as extra terms & conditions */}
      {children != null && <div className={cn("disclaimer")}>{children}</div>}

      {payLaterBreakdownLocalCurrency?.total != null && (
        <div className={cn("disclaimer")}>
          {t("apac.priceBreakdown.payLaterFeesDisclaimer", {
            amount: formatFiatCurrency(
              payLaterBreakdownLocalCurrency.total,
              props.fiatFormatOptions
            ),
          })}
        </div>
      )}
    </div>
  );
};
